import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { useOnboardingStep } from '@/lib/onboarding/tracking'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { toast } from 'sonner'
import {
  ArrowLeftIcon,
  Loader2Icon,
  ScrollTextIcon,
  RotateCwIcon,
  SparklesIcon,
  XIcon,
} from 'lucide-react'
import type {
  MoodboardBrief,
  MoodboardDepartment,
  MoodboardItem,
  MoodboardLogoCandidate,
} from '@backend/lib/db/schema/moodboard'
import { ALL_MOODBOARD_ANALYZERS } from '@backend/lib/db/schema/moodboard'
import { AnalyzeConfigPopover } from './analyze-config'
import type { RemoteFileWithUrl } from '@backend/lib/db/typeRoots/fileWithUrl'
import { trpc, type RouterOutput } from '@/config/trpc'
import { useSidebarStore } from '@/stores/sidebarStore'
import { cn, uploadToS3 } from '@/lib/utils'
import { Button } from '@/components/ui/shadcn/button'
import { Canvas } from './canvas/canvas'
import { AddItemToolbar } from './canvas/add-item-toolbar'
import { DraftCard, type Draft } from './canvas/draft-card'
import { ConnectorPicker } from './canvas/connector-picker'
import { ensureRectVisible } from './canvas/animate-view'
import { CanvasControls } from './canvas/canvas-controls'
import { moodboardHistoryStore, useMoodboardHistory } from './canvas/moodboard-history-store'
import { useCanvasStore, type PendingItem } from './canvas/canvas-store'
import {
  DEFAULT_SIZES,
  centerToTopLeft,
  makeColorItem,
  makeConnectorItem,
  makeFileItem,
  makeTextItem,
  makeUrlItem,
  sizeForFileMime,
  sizeForUrl,
  viewportCenterWorld,
  type ConnectorProvider,
} from './canvas/item-defaults'
import { classifyClipboardData, encodeInternalClip, staggerPosition } from './canvas/paste-dispatch'
import { extractPdfThumbnail } from './canvas/extract-pdf-thumbnail'
import { isFontFile } from '@/lib/fontFamily'
import { parseFontMetadata } from '@/lib/parseFontMetadata'
import {
  readImageDimensions,
  readVideoDimensions,
  fitMediaSize,
} from './canvas/read-media-dimensions'
import { classifyUrl } from '@backend/lib/util/classifyUrl'
import { emitDotRipple } from './canvas/dot-grid'
import { StickyNote } from './canvas/items/sticky-note'
import { LinkCard } from './canvas/items/link-card'
import { FileCard } from './canvas/items/file-card'
import { ColorCard } from './canvas/items/color-card'
import { ConnectorCard } from './canvas/items/connector-card'
import { PendingCard } from './canvas/items/pending-card'
import { AnalysisPanel } from './components/analysis-panel'
import { StreamingAnalysisPanel } from './components/streaming-analysis-panel'
import { OnboardingCanvasTutorial } from './components/onboarding-canvas-tutorial'
import { useOnboardingTutorial } from './use-onboarding-tutorial'
import { useStarterFillStream } from './use-starter-fill-stream'
import {
  findSeeded,
  hasFillableWebsite,
  hasReadableWebsite,
} from '@backend/lib/onboarding/starterBoard'
import { useAnalyzeStream } from './use-analyze-stream'
import {
  computeFitTransform,
  findFreeRect,
  rectsIntersect,
  type Point,
  type Rect,
} from './canvas/geometry'

const EDIT_HINT_SEEN_KEY = 'moodboard_onboarding_edit_hint_seen'

// Editable focus check — paste / backspace should fall through to the focused field.
function isEditableFocus(): boolean {
  const el = document.activeElement as HTMLElement | null
  if (!el) return false
  if (el.isContentEditable) return true
  const tag = el.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

// True while a modal dialog (e.g. the file-card lightbox) is open. Canvas keyboard
// shortcuts must yield to it — otherwise Backspace behind an open lightbox deletes the
// selected card. Radix sets role="dialog" on open Dialog content and unmounts it on
// close, so a present [role="dialog"] means one is open. (Popovers don't use this role.)
function isDialogOpen(): boolean {
  return document.querySelector('[role="dialog"]') !== null
}

// S3 keys of the file-typed items in a snapshot. Used to reconcile assets on leave: keys
// seen during the session but absent from the final state are orphans to delete.
function fileKeysOfItems(items: MoodboardItem[]): string[] {
  return items.filter((i) => i.type === 'file').map((i) => i.file.key)
}

const HEX_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/
// Fallback palette so repeated "Color" clicks stay distinct even when the board has no
// scanned colors to draw from yet.
const FALLBACK_COLORS = [
  '#3366ff',
  '#ef4444',
  '#22c55e',
  '#f59e0b',
  '#a855f7',
  '#14b8a6',
  '#ec4899',
  '#0ea5e9',
]

function normalizeHex(hex: string): string {
  const h = hex.trim().toLowerCase()
  return /^#[0-9a-f]{3}$/.test(h) ? `#${h[1]}${h[1]}${h[2]}${h[2]}${h[3]}${h[3]}` : h
}

// Default color for a directly-placed color card: the first scan-extracted color (from URL
// items) not already on the board as a color card, else the first unused fallback, else a
// hardcoded default. Keeps placed colors unique on the moodboard.
function pickUniqueColor(items: MoodboardItem[]): string {
  const used = new Set(items.filter((i) => i.type === 'color').map((i) => normalizeHex(i.hex)))
  const scanned = items
    .flatMap((i) => (i.type === 'url' ? (i.extractedColors ?? []) : []))
    .filter((h) => HEX_RE.test(h))
  return (
    [...scanned, ...FALLBACK_COLORS].find((h) => !used.has(normalizeHex(h))) ?? FALLBACK_COLORS[0]!
  )
}

function pendingKindForMime(mime: string): PendingItem['kind'] {
  if (mime.startsWith('image/')) return 'image'
  if (mime.startsWith('video/')) return 'video'
  if (mime === 'application/pdf') return 'pdf'
  if (mime.startsWith('font/') || mime.includes('font')) return 'font'
  return 'file'
}

// Read the latest position the user dragged a pending placeholder to. Falls back to
// the spawn position if the pending is no longer in the store (race during upload).
function currentPendingPosition(pendingId: string, fallback: Point): Point {
  const p = useCanvasStore.getState().pendingItems.find((p) => p.id === pendingId)
  return p?.position ?? fallback
}

export function MoodboardDetailPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { id } = useParams<{ id: string }>()
  const personaId = Number(id)
  const badId = Number.isNaN(personaId)
  const {
    data: persona,
    refetch,
    isLoading,
    isError,
  } = trpc.moodboard.getById.useQuery({ id: personaId }, { enabled: !badId })

  // A failed (or never-issued) fetch has to render as a failure. Falling through to
  // the loading branch is what turned a moodboard id that doesn't resolve for the
  // current brand into an endless "Loading…" with no way out.
  if (badId || isError) {
    const inOnboarding = searchParams.get('onboarding') === '1'
    return (
      <section className="mx-auto mt-[10vh] flex max-w-sm flex-col items-center gap-3 p-6 text-center">
        <h2 className="text-foreground text-base font-medium">We couldn't open this moodboard</h2>
        <p className="text-muted-foreground text-sm">
          It may have been deleted, or it belongs to a different brand.
        </p>
        <Button
          variant="outline"
          onClick={() => navigate(inOnboarding ? '/company-setup' : '/moodboards')}>
          {inOnboarding ? 'Back to setup' : 'Back to moodboards'}
        </Button>
      </section>
    )
  }

  if (isLoading || !persona) {
    return (
      <section className="text-muted-foreground mt-[10vh] flex w-full justify-center p-6 text-sm">
        Loading…
      </section>
    )
  }
  return <Loaded persona={persona} refetch={refetch} />
}

type LoadedPersona = RouterOutput['moodboard']['getById']
type LoadedProps = {
  persona: LoadedPersona
  refetch: () => Promise<unknown>
}

function Loaded({ persona, refetch }: LoadedProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  // Onboarding mode: the canvas was entered from the onboarding flow. Swaps the
  // Analyze button for a "Finalize & continue" CTA (which hands off to the finish
  // steps, where the brief is generated) and shows the first-run tutorial.
  const isOnboarding = searchParams.get('onboarding') === '1'
  const { isOpen: sidebarOpen } = useSidebarStore()
  const { mutateAsync: update } = trpc.moodboard.update.useMutation()
  const { mutateAsync: appendItems } = trpc.moodboard.appendItems.useMutation()
  const { mutateAsync: getUploadUrl } = trpc.asset.getUploadUrl.useMutation()
  const { mutateAsync: addLogoFromCandidate } = trpc.moodboard.addLogoFromCandidate.useMutation()
  const { mutateAsync: cleanupAssets } = trpc.moodboard.cleanupAssets.useMutation()
  const utils = trpc.useUtils()
  // Live connector health. Only fetched when the board actually has connectors, since it costs
  // a GitHub round-trip. Derived rather than stored: a connection can disappear without CASSI
  // being involved, so a stale flag would show a healthy card for a dead connector.
  const hasConnectors = persona.items.some((i) => i.type === 'connector')
  const { data: connectorStatus } = trpc.brandConnection.connectorStatus.useQuery(undefined, {
    enabled: hasConnectors,
    staleTime: 60_000,
  })
  const connectorStatusFor = (item: Extract<MoodboardItem, { type: 'connector' }>) => {
    if (!connectorStatus) return 'unknown' as const
    if (item.provider === 'github') {
      const gh = connectorStatus.github
      if (!gh) return 'no-connection' as const
      if (!gh.valid) return 'connection-invalid' as const
      return gh.repositories.some((r) => `${r.owner}/${r.name}` === item.target)
        ? ('ok' as const)
        : ('not-covered' as const)
    }
    // Google Calendar: same trade-off as Notion below. A revoked grant or a calendar the
    // account lost access to reports through the tool at read time.
    if (item.provider === 'google_calendar') {
      return connectorStatus.googleCalendar ? ('ok' as const) : ('no-connection' as const)
    }
    // Notion: a page id says nothing about which workspace it belongs to, and verifying it
    // costs an API call per target. Missing connection is the case worth catching; a target
    // that moved workspaces reports through the tool at read time instead.
    return connectorStatus.notion ? ('ok' as const) : ('no-connection' as const)
  }
  const history = useMoodboardHistory()
  const { mutateAsync: enrichUrlItem } = trpc.moodboard.enrichUrlItem.useMutation()
  const { mutateAsync: enrichVideoItem } = trpc.moodboard.enrichVideoItem.useMutation()
  const enrichingIds = useCanvasStore((s) => s.enrichingIds)

  const canvasStep = useOnboardingStep('canvas', isOnboarding)
  const isEnriching = enrichingIds.size > 0
  const tutorial = useOnboardingTutorial()
  // Once the user has edited one card, the "double-click to edit" captions go away.
  const [editHintSeen, setEditHintSeen] = useState(() => {
    try {
      return localStorage.getItem(EDIT_HINT_SEEN_KEY) === '1'
    } catch {
      return false
    }
  })
  const markEditHintSeen = () => {
    if (editHintSeen) return
    try {
      localStorage.setItem(EDIT_HINT_SEEN_KEY, '1')
    } catch {
      // ignore — the hint just shows again next visit
    }
    setEditHintSeen(true)
  }
  const [finalizeClicked, setFinalizeClicked] = useState(false)
  const markEnriching = useCanvasStore((s) => s.markEnriching)
  const clearEnriching = useCanvasStore((s) => s.clearEnriching)
  const setJustPlacedColorId = useCanvasStore((s) => s.setJustPlacedColorId)
  // The unpersisted card the user is currently typing into (Note/Instruction/Link), if any.
  const [draft, setDraft] = useState<Draft | null>(null)
  /** Connector item whose target is being re-picked; its card is replaced by the picker. */
  const [reselectingConnectorId, setReselectingConnectorId] = useState<string | null>(null)
  // Which analyzers run on Analyze. Seeded from the persisted per-board selection (null =
  // all). Local state is the session's source of truth; toggling also persists for next time.
  const [enabledAnalyzers, setEnabledAnalyzers] = useState<MoodboardDepartment[]>(
    persona.enabledAnalyzers ?? ALL_MOODBOARD_ANALYZERS,
  )
  const toggleAnalyzer = (id: MoodboardDepartment) => {
    const on = enabledAnalyzers.includes(id)
    if (on && enabledAnalyzers.length === 1) return // keep at least one on
    const next = on
      ? enabledAnalyzers.filter((d) => d !== id)
      : ALL_MOODBOARD_ANALYZERS.filter((d) => enabledAnalyzers.includes(d) || d === id)
    setEnabledAnalyzers(next)
    void update({ id: persona.id, enabledAnalyzers: next })
  }
  const { state: analyzeState, start: startAnalyze } = useAnalyzeStream(persona.id)
  // Stays true from the moment Analyze is clicked until the refetched persona row
  // includes the new brief. Prevents an empty / stale flash between the stream's
  // own `done` event and the tRPC query landing.
  const [showStreaming, setShowStreaming] = useState(false)
  const analyzing =
    analyzeState.phase !== 'idle' && analyzeState.phase !== 'done' && analyzeState.phase !== 'error'

  const pendingItems = useCanvasStore((s) => s.pendingItems)
  const addPending = useCanvasStore((s) => s.addPending)
  const removePending = useCanvasStore((s) => s.removePending)
  const viewportWidth = useCanvasStore((s) => s.viewport.width)
  const viewportHeight = useCanvasStore((s) => s.viewport.height)
  const setTransform = useCanvasStore((s) => s.setTransform)
  const dragOverrides = useCanvasStore((s) => s.dragOverrides)

  const [nameDraft, setNameDraft] = useState<string | undefined>(undefined)
  // The name renders as plain text until clicked; only then does it become an input.
  // A rendered <input> carries a browser-enforced minimum width, which kept the field
  // from shrinking to short names — a text node has no such floor.
  const [editingName, setEditingName] = useState(false)
  // Analysis sidebar is collapsed by default; auto-opens when an analyze finishes.
  const [analysisOpen, setAnalysisOpen] = useState(false)
  // Resizeable width — drag the left edge of the sidebar. Persisted across reloads.
  const ANALYSIS_MIN = 320
  const ANALYSIS_MAX = 800
  const ANALYSIS_DEFAULT = 440
  const [analysisWidth, setAnalysisWidth] = useState<number>(() => {
    if (typeof window === 'undefined') return ANALYSIS_DEFAULT
    const stored = Number(window.localStorage.getItem('moodboard-analysis-width'))
    if (!Number.isFinite(stored) || stored <= 0) return ANALYSIS_DEFAULT
    return Math.max(ANALYSIS_MIN, Math.min(ANALYSIS_MAX, stored))
  })
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef<{ x: number; w: number } | null>(null)
  const onResizeStart = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsResizing(true)
    resizeStartRef.current = { x: e.clientX, w: analysisWidth }
    e.currentTarget.setPointerCapture(e.pointerId)
  }
  const onResizeMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const start = resizeStartRef.current
    if (!start) return
    const delta = start.x - e.clientX
    const next = Math.max(ANALYSIS_MIN, Math.min(ANALYSIS_MAX, start.w + delta))
    setAnalysisWidth(next)
  }
  const onResizeEnd = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!resizeStartRef.current) return
    resizeStartRef.current = null
    setIsResizing(false)
    e.currentTarget.releasePointerCapture(e.pointerId)
    try {
      window.localStorage.setItem('moodboard-analysis-width', String(analysisWidth))
    } catch {
      // localStorage unavailable — drop the persistence silently.
    }
  }

  // Auto-fit the canvas view the first time a persona is loaded. Subsequent pans / zooms
  // by the user are respected — we won't reset their view just because items moved.
  const fittedForId = useRef<number | null>(null)
  useEffect(() => {
    if (fittedForId.current === persona.id) return
    if (viewportWidth === 0 || viewportHeight === 0) return
    const rects = persona.items.map((i) => ({
      x: i.position.x,
      y: i.position.y,
      w: i.size.w,
      h: i.size.h,
    }))
    const fit = computeFitTransform(rects, { width: viewportWidth, height: viewportHeight })
    if (fit) setTransform({ scale: fit.scale, offset: fit.offset })
    fittedForId.current = persona.id
  }, [persona.id, persona.items, viewportWidth, viewportHeight, setTransform])

  // Undo/redo session lifecycle. Seed history with the loaded items on entry; on leave
  // (unmount or switching moodboards), reconcile assets — delete S3 files that were seen
  // during the session but aren't in the final state — and clear the history. Keyed on
  // persona.id only so the seed runs once per moodboard, not on every item change.
  useEffect(() => {
    const moodboardId = persona.id
    moodboardHistoryStore.getState().reset(persona.items)
    return () => {
      const { data, cursor } = moodboardHistoryStore.getState()
      const finalKeys = new Set(fileKeysOfItems(data[cursor] ?? []))
      const seenKeys = new Set(data.flatMap(fileKeysOfItems))
      const orphans = [...seenKeys].filter((k) => !finalKeys.has(k))
      if (orphans.length > 0) {
        cleanupAssets({ id: moodboardId, keys: orphans }).catch(() => {
          // Best-effort on leave; a future GC can sweep anything missed.
        })
      }
      moodboardHistoryStore.getState().reset(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona.id])

  const items = persona.items
  const stale =
    !!persona.analysisHash &&
    !!persona.analysisGeneratedAt &&
    new Date(persona.updatedAt).getTime() > new Date(persona.analysisGeneratedAt).getTime()

  const spawnAt = (size: { w: number; h: number }) => {
    const s = useCanvasStore.getState()
    const center = viewportCenterWorld({ viewport: s.viewport, scale: s.scale, offset: s.offset })
    return placeNear(centerToTopLeft(center, size), size)
  }

  // Wraps findFreeRect with the current item set so callers don't have to
  // assemble the avoid-list. Used by spawnAt + drop/paste anchors so a new
  // item drops into open space when its requested position would overlap an
  // existing card.
  const placeNear = (desiredTopLeft: Point, size: { w: number; h: number }): Point => {
    const otherRects: Rect[] = items.map((i) => ({
      x: i.position.x,
      y: i.position.y,
      w: i.size.w,
      h: i.size.h,
    }))
    const free = findFreeRect(
      { x: desiredTopLeft.x, y: desiredTopLeft.y, w: size.w, h: size.h },
      otherRects,
    )
    return { x: free.x, y: free.y }
  }

  // Writes the full items array (full-replace mutation + refetch). Records an undo step by
  // default — this is the single commit choke point for moves/resizes/edits/deletes, so
  // recording here (and not on the transient drag/color/text drafts that never reach it)
  // captures exactly the committed states. Undo/redo call it with record:false so applying
  // a snapshot doesn't itself push a new step.
  const writeItems = async (next: MoodboardItem[], opts?: { record?: boolean }) => {
    await update({ id: persona.id, items: next })
    await refetch()
    if (opts?.record !== false) moodboardHistoryStore.getState().push(next)
  }

  const persistItems = (next: MoodboardItem[]) => writeItems(next)

  // Adds go through the race-safe `appendItems`, not `writeItems`, so record the resulting
  // authoritative items after the refetch settles.
  const refetchAndRecordHistory = async () => {
    await refetch()
    const data = utils.moodboard.getById.getData({ id: persona.id })
    if (data) moodboardHistoryStore.getState().push(data.items)
  }

  // Background enrichment mutates items outside a user action; sync the current history
  // node (don't push a step) so undoing a later edit doesn't revert enrichment.
  const refetchAndSyncHistory = async () => {
    await refetch()
    const data = utils.moodboard.getById.getData({ id: persona.id })
    if (data) moodboardHistoryStore.getState().syncCurrent(data.items)
  }

  // Undo/redo move the history cursor and write the target snapshot back (record:false so
  // applying it doesn't push a new step). Assets removed during the session are still in S3
  // (cleanup is deferred to leave), so restored file cards keep their images.
  const handleUndo = async () => {
    const snapshot = moodboardHistoryStore.getState().undo()
    if (snapshot) await writeItems(snapshot, { record: false })
  }
  const handleRedo = async () => {
    const snapshot = moodboardHistoryStore.getState().redo()
    if (snapshot) await writeItems(snapshot, { record: false })
  }

  const updateItem = (id: string, patch: Partial<MoodboardItem>) =>
    persistItems(items.map((i) => (i.id === id ? ({ ...i, ...patch } as MoodboardItem) : i)))

  const uploadFile = async (file: File): Promise<RemoteFileWithUrl> => {
    const { key, uploadUrl, assetUrl } = await getUploadUrl({
      type: 'moodboard',
      moodboardId: persona.id,
    })
    await uploadToS3(uploadUrl, file)
    return {
      source: 'remote',
      url: assetUrl,
      key,
      file: { name: file.name, type: file.type, size: file.size },
    }
  }

  // Kicks off any background-enrichment work for a freshly-added item (URL scrape /
  // colors / logos, or video Gemini analysis). Fire-and-forget — the backend re-reads
  // the persona before writing so a delete during the in-flight period is harmless.
  const kickoffEnrichment = (item: MoodboardItem) => {
    if (item.type === 'url') {
      // Embed URLs (YouTube / Vimeo / Spotify / Pinterest) skip scrape + colors +
      // logos — the embed IS the content, page-level chrome would mislead the
      // analyzer. The agent sees them labelled as "{kind} embed" instead.
      if (classifyUrl(item.url).kind !== 'plain') return
      markEnriching(item.id)
      enrichUrlItem({ id: persona.id, itemId: item.id })
        .then(() => refetchAndSyncHistory())
        .catch(() => {
          // Best-effort; the orchestrator will catch this work up on the next Analyze.
        })
        .finally(() => clearEnriching(item.id))
      return
    }
    if (item.type === 'file' && item.file.file.type?.startsWith('video/')) {
      markEnriching(item.id)
      enrichVideoItem({ id: persona.id, itemId: item.id })
        .then(() => refetchAndSyncHistory())
        .catch(() => {})
        .finally(() => clearEnriching(item.id))
    }
  }

  // Auto-enrich URL items that arrived without a scrape (seeded via the create
  // mutation during onboarding, or via MCP) — items added through the canvas
  // already enrich on drop. Runs once on mount so a freshly-seeded onboarding
  // board scrapes its website immediately instead of waiting for Analyze.
  const autoEnrichedRef = useRef(false)
  useEffect(() => {
    if (autoEnrichedRef.current) return
    autoEnrichedRef.current = true
    for (const item of persona.items) {
      if (
        item.type === 'url' &&
        classifyUrl(item.url).kind === 'plain' &&
        item.scrapedContent == null &&
        !enrichingIds.has(item.id)
      ) {
        kickoffEnrichment(item)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Onboarding starter fill: once the seeded website has been read, rewrite the
  // untouched starter notes from it and swap the invitation for its logo and colors
  // (server side, streamed so the notes type in). Runs once per visit; only items
  // still carrying a `generic` seed are touched, here and on the server.
  const seededWebsite = isOnboarding ? findSeeded(items, 'website') : undefined
  const hasGenericStarter = items.some(
    (i) =>
      i.seed?.source === 'generic' &&
      (i.seed.slot === 'voice' || i.seed.slot === 'audience' || i.seed.slot === 'invite'),
  )
  const websiteFillable = hasFillableWebsite(seededWebsite)
  const websiteReadable = hasReadableWebsite(seededWebsite)
  const websiteReading =
    seededWebsite?.type === 'url' &&
    classifyUrl(seededWebsite.url).kind === 'plain' &&
    (enrichingIds.has(seededWebsite.id) || seededWebsite.scrapedContent == null)
  const { state: starterFill, start: startStarterFill } = useStarterFillStream(
    persona.id,
    refetchAndSyncHistory,
  )
  const starterFillStartedRef = useRef(false)
  useEffect(() => {
    if (!isOnboarding || !websiteFillable || !hasGenericStarter) return
    if (starterFillStartedRef.current) return
    starterFillStartedRef.current = true
    void startStarterFill()
  }, [isOnboarding, websiteFillable, hasGenericStarter, startStarterFill])
  const fillRunning = starterFill.phase === 'streaming'
  const fillPending =
    isOnboarding &&
    hasGenericStarter &&
    (websiteReading || fillRunning || (websiteFillable && starterFill.phase === 'idle'))

  // What a starter card shows while the fill is pending: placeholder lines while the
  // website is read, then the generated text typing in. Undefined = render normally.
  const starterNoteFill = (item: MoodboardItem) => {
    if (!fillPending || item.seed?.source !== 'generic') return undefined
    const slot = item.seed.slot
    if (slot === 'invite') {
      return {
        status: 'waiting' as const,
        text: '',
        label: fillRunning ? 'Adding your logo and colors…' : 'Looking for your logo and colors…',
      }
    }
    if (slot !== 'voice' && slot !== 'audience') return undefined
    // The text scrape failed: the fill only adds the logo and colors, the notes stay.
    if (!websiteReading && !websiteReadable) return undefined
    return fillRunning
      ? {
          status: 'streaming' as const,
          text: starterFill.partial[slot] ?? '',
          label: 'Writing from your website…',
        }
      : { status: 'waiting' as const, text: '', label: 'Reading your website…' }
  }

  // Onboarding: Finalize waits for the website read and the starter fill so the
  // brief sees them, but never for long. A slow site used to hold the button
  // indefinitely. After the cap the user may continue; the analyze stream on the
  // finish page scrapes any item that is still missing its content, and the starter
  // notes just keep their generic copy (which the brief ignores).
  const FINALIZE_WAIT_CAP_MS = 30_000
  const [finalizeWaitCapped, setFinalizeWaitCapped] = useState(false)
  const finalizeBusy = isEnriching || fillPending
  useEffect(() => {
    if (!isOnboarding || !finalizeBusy || finalizeWaitCapped) return
    const timer = setTimeout(() => {
      setFinalizeWaitCapped(true)
      canvasStep.track('onboarding_enrich_wait_capped', {
        enriching: enrichingIds.size,
        filling: fillPending,
      })
    }, FINALIZE_WAIT_CAP_MS)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- restart only when the wait starts/stops
  }, [isOnboarding, finalizeBusy, finalizeWaitCapped])
  const finalizeBlocked = finalizeBusy && !finalizeWaitCapped
  const finalizeBusyLabel = websiteReading
    ? 'Reading your website…'
    : fillPending
      ? 'Preparing your board…'
      : `Enriching ${enrichingIds.size}…`
  // Point at the next step once there is nothing left to wait for.
  const nudgeFinalize = isOnboarding && !tutorial.open && !finalizeBlocked && !finalizeClicked

  // Show pending placeholders immediately, then run `work` (upload + persist) and clear
  // the pendings when it settles. Cleanup runs in finally so Object URLs always get revoked.
  // Fires a dot-grid ripple at each newly-persisted item's center after the persist lands.
  //
  // Uses the race-safe `appendItems` mutation rather than the full-replace `update`
  // path. Why: if the user drops item B while item A's background enrichment is in
  // flight, the captured-at-drop-time `items` closure here would be missing the
  // enrichment fields by the time `work()` resolves — and a full-array write would
  // clobber whatever the enrichment mutation wrote to the row in the meantime.
  // Server-side concat preserves everything outside the new items.
  const runWithPending = async (
    pendings: PendingItem[],
    work: () => Promise<MoodboardItem[]>,
    cleanup?: () => void,
  ) => {
    pendings.forEach((p) => addPending(p))
    try {
      const newItems = await work()
      if (newItems.length > 0) {
        await appendItems({ id: persona.id, items: newItems })
        await refetchAndRecordHistory()
        newItems.forEach(ripplePersistedItem)
        newItems.forEach(kickoffEnrichment)
      }
    } finally {
      pendings.forEach((p) => removePending(p.id))
      cleanup?.()
    }
  }

  // Note/Instruction: drop an editable draft card and let the user type. Position is chosen
  // now so it lands in free space; it becomes a real item on commit (see DraftCard).
  const onAddText = (kind: 'reference' | 'instruction') => {
    const position = spawnAt(DEFAULT_SIZES.text)
    setDraft({ id: crypto.randomUUID(), kind: 'text', textKind: kind, position })
    ensureRectVisible({ x: position.x, y: position.y, ...DEFAULT_SIZES.text })
  }

  const commitTextItem = async (
    text: string,
    kind: 'reference' | 'instruction',
    position: Point,
  ) => {
    const pending: PendingItem = {
      id: crypto.randomUUID(),
      position,
      size: DEFAULT_SIZES.text,
      kind: 'text',
      label: text.slice(0, 40),
    }
    await runWithPending([pending], async () => [
      makeTextItem({ text, kind, position: currentPendingPosition(pending.id, position) }),
    ])
  }

  // Link: draft card with a URL input. The scrape can't start until a URL exists, so
  // enrichment kicks off on commit (runWithPending → kickoffEnrichment), not on placement.
  const onAddUrl = () => {
    const position = spawnAt(DEFAULT_SIZES.url)
    setDraft({ id: crypto.randomUUID(), kind: 'url', position })
    ensureRectVisible({ x: position.x, y: position.y, ...DEFAULT_SIZES.url })
  }

  const commitUrlItem = async (url: string, position: Point) => {
    const pending: PendingItem = {
      id: crypto.randomUUID(),
      position,
      size: sizeForUrl(url),
      kind: 'url',
      label: url,
    }
    await runWithPending([pending], async () => [
      makeUrlItem({ url, position: currentPendingPosition(pending.id, position) }),
    ])
  }

  // Commit or discard the active draft (Note/Instruction/Link). Empty input → discard.
  const handleDraftCommit = (value: string) => {
    const d = draft
    setDraft(null)
    if (!d) return
    if (d.kind === 'text') void commitTextItem(value, d.textKind, d.position)
    else if (d.kind === 'url') void commitUrlItem(value, d.position)
    else {
      // Connector commit carries JSON {target,label} so the opaque Notion id keeps a title.
      const { target, label } = JSON.parse(value) as { target: string; label?: string }
      void commitConnectorItem(d.provider, target, label, d.position)
    }
  }

  // Places a color card directly with a smart, unique default, then flags it so its
  // ColorCard opens the picker on mount — the user refines the color on the card itself.
  const onAddColor = async () => {
    const hex = pickUniqueColor(items)
    const colorId = crypto.randomUUID()
    const initialPos = spawnAt(DEFAULT_SIZES.color)
    ensureRectVisible({ x: initialPos.x, y: initialPos.y, ...DEFAULT_SIZES.color })
    const pending: PendingItem = {
      id: crypto.randomUUID(),
      position: initialPos,
      size: DEFAULT_SIZES.color,
      kind: 'color',
      label: hex,
    }
    setJustPlacedColorId(colorId)
    await runWithPending([pending], async () => [
      makeColorItem({ id: colorId, hex, position: currentPendingPosition(pending.id, initialPos) }),
    ])
  }

  // Connector: drop a draft card with a target picker (GitHub repos / Notion pages); commits
  // to a real connector item on pick. Mirrors the Note/Link draft flow.
  const onAddConnector = (provider: ConnectorProvider) => {
    const position = spawnAt(DEFAULT_SIZES.connectorDraft)
    setDraft({ id: crypto.randomUUID(), kind: 'connector', provider, position })
    ensureRectVisible({ x: position.x, y: position.y, ...DEFAULT_SIZES.connectorDraft })
  }

  const commitConnectorItem = async (
    provider: ConnectorProvider,
    target: string,
    label: string | undefined,
    position: Point,
  ) => {
    const item = makeConnectorItem({ provider, target, label, position })
    await appendItems({ id: persona.id, items: [item] })
    await refetchAndRecordHistory()
  }

  // Swap an existing connector's target in place. The picker replaces the card while open, so
  // the item keeps its id, position and annotation — this is "point this somewhere else",
  // not "delete and re-add".
  const commitConnectorReselect = (itemId: string, value: string) => {
    setReselectingConnectorId(null)
    const { target, label } = JSON.parse(value) as { target: string; label?: string }
    void updateItem(itemId, { target, label })
  }

  const onUploadFiles = async (files: File[]) => {
    if (files.length === 0) return
    // Anchor the batch off the first file's default size; addFilesAt staggers the
    // rest so multi-select drops don't stack on one spot.
    const size = sizeForFileMime(files[0]!.type, files[0]!.name)
    const anchor = spawnAt(size)
    ensureRectVisible({ x: anchor.x, y: anchor.y, w: size.w, h: size.h })
    await addFilesAt(files, anchor)
  }

  const addFilesAt = async (files: File[], anchor: Point) => {
    // Build plans with aspect-ratio-aware sizing for images. The dimension read
    // uses a transient Object URL and resolves before persist, so the pending
    // card already lands at roughly its final size — saves the user a manual
    // resize after every drop. Falls back to the static `sizeForFileMime`
    // default if the read fails (e.g. corrupt image, SVG).
    const plans = await Promise.all(
      files.map(async (file, i) => {
        let size = sizeForFileMime(file.type, file.name)
        if (file.type.startsWith('image/') && file.type !== 'image/svg+xml') {
          const dims = await readImageDimensions(file).catch(() => null)
          if (dims) size = fitMediaSize(dims)
        } else if (file.type.startsWith('video/')) {
          const dims = await readVideoDimensions(file).catch(() => null)
          if (dims) size = fitMediaSize(dims)
        }
        const initialPos = placeNear(staggerPosition(centerToTopLeft(anchor, size), i), size)
        const blobUrl =
          file.type.startsWith('image/') || file.type.startsWith('video/')
            ? URL.createObjectURL(file)
            : undefined
        const pending: PendingItem = {
          id: crypto.randomUUID(),
          position: initialPos,
          size,
          kind: pendingKindForMime(file.type),
          previewUrl: blobUrl,
          label: file.name,
        }
        return { pending, file, blobUrl, initialPos }
      }),
    )

    // Track thumbnail blob URLs we create so we can revoke them on cleanup.
    const thumbnailLocalUrls: string[] = []

    await runWithPending(
      plans.map((p) => p.pending),
      async () => {
        const uploads = await Promise.all(
          plans.map(async (p) => {
            const isPdf = p.file.type === 'application/pdf'
            // For PDFs: race a client-side page-1 thumbnail render with the main file
            // upload. As soon as the thumbnail blob is ready, update the pending card
            // so the user sees the actual page instead of the spinner — then upload it
            // as a sibling asset so the persisted item can keep using it.
            const thumbnailPromise = isPdf
              ? extractPdfThumbnail(p.file)
                  .then((blob) => {
                    const localUrl = URL.createObjectURL(blob)
                    thumbnailLocalUrls.push(localUrl)
                    useCanvasStore.getState().updatePending(p.pending.id, { previewUrl: localUrl })
                    return blob
                  })
                  .catch((err) => {
                    console.warn('[pdf-thumb] extract failed', err)
                    return null
                  })
              : Promise.resolve(null)

            // Font files: parse the real family/weight/style from the binary in
            // parallel with the upload, so the item stores the true typeface name.
            const fontMetaPromise = isFontFile(p.file.type, p.file.name)
              ? parseFontMetadata(p.file)
              : Promise.resolve(null)

            const [remote, thumbnailBlob, fontMeta] = await Promise.all([
              uploadFile(p.file),
              thumbnailPromise,
              fontMetaPromise,
            ])

            let thumbnailUrl: string | undefined
            if (thumbnailBlob) {
              const thumbFile = new File([thumbnailBlob], `${p.file.name}.thumb.png`, {
                type: 'image/png',
              })
              try {
                const thumbRemote = await uploadFile(thumbFile)
                thumbnailUrl = thumbRemote.url
                console.debug('[pdf-thumb] uploaded', { url: thumbnailUrl })
              } catch (err) {
                console.warn('[pdf-thumb] upload failed', err)
              }
            }
            return { plan: p, remote, thumbnailUrl, fontMeta }
          }),
        )
        return uploads.map(({ plan, remote, thumbnailUrl, fontMeta }) =>
          makeFileItem({
            file: remote,
            thumbnailUrl,
            fontMeta: fontMeta ?? undefined,
            position: currentPendingPosition(plan.pending.id, plan.initialPos),
            // Pass the computed-at-drop-time size through so persisted items
            // match the pending card (aspect-ratio-aware for images).
            size: plan.pending.size,
          }),
        )
      },
      () => {
        plans.forEach((p) => p.blobUrl && URL.revokeObjectURL(p.blobUrl))
        thumbnailLocalUrls.forEach((u) => URL.revokeObjectURL(u))
      },
    )
  }

  const handlePayload = async (
    payload: ReturnType<typeof classifyClipboardData>,
    anchor: Point,
  ) => {
    if (payload.kind === 'empty') return
    if (payload.kind === 'text') {
      const initialPos = placeNear(centerToTopLeft(anchor, DEFAULT_SIZES.text), DEFAULT_SIZES.text)
      const pending: PendingItem = {
        id: crypto.randomUUID(),
        position: initialPos,
        size: DEFAULT_SIZES.text,
        kind: 'text',
        label: payload.text.slice(0, 40),
      }
      await runWithPending([pending], async () => [
        makeTextItem({
          text: payload.text,
          kind: 'reference',
          position: currentPendingPosition(pending.id, initialPos),
        }),
      ])
      return
    }
    if (payload.kind === 'url') {
      const size = sizeForUrl(payload.url)
      const initialPos = placeNear(centerToTopLeft(anchor, size), size)
      const pending: PendingItem = {
        id: crypto.randomUUID(),
        position: initialPos,
        size,
        kind: 'url',
        label: payload.url,
      }
      await runWithPending([pending], async () => [
        makeUrlItem({ url: payload.url, position: currentPendingPosition(pending.id, initialPos) }),
      ])
      return
    }
    if (payload.kind === 'internal') {
      pasteCanvasItems(payload.items, anchor)
      return
    }
    await addFilesAt(payload.files, anchor)
  }

  // Window paste — opt out if a form field is focused.
  useEffect(() => {
    const handler = async (e: ClipboardEvent) => {
      if (isEditableFocus()) return
      if (!e.clipboardData) return
      const intent = classifyClipboardData(e.clipboardData)
      if (intent.kind === 'empty') return
      e.preventDefault()
      const s = useCanvasStore.getState()
      const anchor = viewportCenterWorld({ viewport: s.viewport, scale: s.scale, offset: s.offset })
      await handlePayload(intent, anchor)
    }
    window.addEventListener('paste', handler)
    return () => window.removeEventListener('paste', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, persona.id])

  // Backspace / Delete deletes the current selection. Opt out if a form field is focused.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableFocus()) return
      // Don't delete the card behind an open lightbox / dialog.
      if (isDialogOpen()) return
      if (e.key !== 'Backspace' && e.key !== 'Delete') return
      const selected = useCanvasStore.getState().selectedIds
      if (selected.size === 0) return
      e.preventDefault()
      const remaining = items.filter((i) => !selected.has(i.id))
      if (remaining.length === items.length) return
      void persistItems(remaining)
      useCanvasStore.getState().setSelected(new Set())
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, persona.id])

  // Cmd/Ctrl+Z = undo, Cmd/Ctrl+Shift+Z = redo. Same guards as delete so it doesn't fire
  // while typing in a field or with a dialog open.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (isEditableFocus()) return
      if (isDialogOpen()) return
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return
      e.preventDefault()
      if (e.shiftKey) void handleRedo()
      else void handleUndo()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona.id])

  // Duplicates items onto the pile. The canvas-internal paste path: copies originate
  // from our cmd+C copy event (which writes a magic JSON to the system clipboard),
  // and round-trip back through the regular paste handler.
  //
  // Positioning: anchor on the group centroid → translate the whole group to the
  // viewport center + a stagger offset that grows per consecutive paste from the
  // same copy, so repeated cmd+V doesn't stack.
  const pasteCanvasItems = async (sourceItems: MoodboardItem[], anchor: Point) => {
    if (sourceItems.length === 0) return

    // Centroid of the source items (avg of each item's bbox center).
    const cx =
      sourceItems.reduce((s, src) => s + src.position.x + src.size.w / 2, 0) / sourceItems.length
    const cy =
      sourceItems.reduce((s, src) => s + src.position.y + src.size.h / 2, 0) / sourceItems.length
    const tx0 = anchor.x - cx
    const ty0 = anchor.y - cy

    // Initial duplicates with centroid translated to the anchor.
    let duplicates: MoodboardItem[] = sourceItems.map((src) => ({
      ...src,
      id: crypto.randomUUID(),
      createdAt: new Date().toISOString(),
      position: { x: src.position.x + tx0, y: src.position.y + ty0 },
    }))

    // Place the group as a whole: compute the bounding box, find free space
    // for it, translate every duplicate by the resulting delta. Preserves the
    // group's relative layout while avoiding overlap with existing items.
    const minX = Math.min(...duplicates.map((d) => d.position.x))
    const minY = Math.min(...duplicates.map((d) => d.position.y))
    const maxX = Math.max(...duplicates.map((d) => d.position.x + d.size.w))
    const maxY = Math.max(...duplicates.map((d) => d.position.y + d.size.h))
    const bbox: Rect = { x: minX, y: minY, w: maxX - minX, h: maxY - minY }
    const otherRects: Rect[] = items.map((i) => ({
      x: i.position.x,
      y: i.position.y,
      w: i.size.w,
      h: i.size.h,
    }))
    const free = findFreeRect(bbox, otherRects)
    const extraDx = free.x - bbox.x
    const extraDy = free.y - bbox.y
    if (extraDx !== 0 || extraDy !== 0) {
      duplicates = duplicates.map((d) => ({
        ...d,
        position: { x: d.position.x + extraDx, y: d.position.y + extraDy },
      }))
    }

    // Use the race-safe append so a concurrent enrichment write doesn't get
    // clobbered by a stale items snapshot.
    await appendItems({ id: persona.id, items: duplicates })
    await refetchAndRecordHistory()
    useCanvasStore.getState().setSelected(new Set(duplicates.map((d) => d.id)))
    duplicates.forEach((d) =>
      emitDotRipple({ x: d.position.x, y: d.position.y, w: d.size.w, h: d.size.h }),
    )
  }

  // cmd/ctrl + C — write the selection to the system clipboard with a magic prefix.
  // The paste handler above detects the prefix and round-trips it back to
  // `pasteCanvasItems`. Using the OS clipboard (not a private store) means anything
  // else the user copies in or out of the app cleanly overrides our payload.
  useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      if (isEditableFocus()) return
      if (!e.clipboardData) return
      const selected = useCanvasStore.getState().selectedIds
      const toCopy = items.filter((i) => selected.has(i.id))
      if (toCopy.length === 0) return
      e.preventDefault()
      e.clipboardData.setData('text/plain', encodeInternalClip(toCopy))
      useCanvasStore.getState().resetPasteCount()
      toast.success(`Copied ${toCopy.length} item${toCopy.length === 1 ? '' : 's'}`)
    }
    window.addEventListener('copy', handler)
    return () => window.removeEventListener('copy', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, persona.id])

  const handleDrop = async (worldPosition: Point, data: DataTransfer) => {
    const intent = classifyClipboardData(data)
    await handlePayload(intent, worldPosition)
  }

  // Marquee → find all items whose bounding box intersects the user's rectangle. A plain
  // marquee makes them the new selection; a shift-marquee toggles each boxed item against
  // the current selection (adds unselected, removes selected).
  const handleMarquee = (worldRect: Rect, toggle: boolean) => {
    const hits = items
      .filter((i) =>
        rectsIntersect(worldRect, {
          x: i.position.x,
          y: i.position.y,
          w: i.size.w,
          h: i.size.h,
        }),
      )
      .map((i) => i.id)
    const store = useCanvasStore.getState()
    if (toggle) {
      const next = new Set(store.selectedIds)
      hits.forEach((id) => (next.has(id) ? next.delete(id) : next.add(id)))
      store.setSelected(next)
    } else {
      store.setSelected(new Set(hits))
    }
  }

  // Commits a position for a single item OR — when that item is part of a multi-selection
  // — for the whole selection in a single tRPC update. Reads the live drag overrides for
  // the other selected items so we capture whatever positions the drag hook published
  // during the gesture. Also fires a dot-grid ripple at the dropped item's center.
  const commitItemMove = async (itemId: string, finalPosition: Point) => {
    const store = useCanvasStore.getState()
    const selected = store.selectedIds
    const target = items.find((i) => i.id === itemId)
    if (target) {
      emitDotRipple({ x: finalPosition.x, y: finalPosition.y, w: target.size.w, h: target.size.h })
    }
    if (!selected.has(itemId) || selected.size <= 1) {
      await updateItem(itemId, { position: finalPosition })
      return
    }
    const next = items.map((i) => {
      if (i.id === itemId) return { ...i, position: finalPosition }
      if (!selected.has(i.id)) return i
      const override = store.dragOverrides[i.id]
      if (override) {
        // Multi-drag siblings also emit a ripple from their dropped rect.
        emitDotRipple({ x: override.position.x, y: override.position.y, w: i.size.w, h: i.size.h })
        return { ...i, position: override.position }
      }
      return i
    })
    await persistItems(next)
  }

  // Fire a ripple at a newly-spawned item's bbox. Used by all add paths (toolbar,
  // paste, drop) so the canvas reacts to every new arrival, scaled to item size.
  const ripplePersistedItem = (item: MoodboardItem) => {
    emitDotRipple({ x: item.position.x, y: item.position.y, w: item.size.w, h: item.size.h })
  }

  const saveName = async () => {
    if (nameDraft === undefined) return
    const next = nameDraft.trim() || 'Untitled context'
    setNameDraft(undefined)
    if (next === persona.name) return
    await update({ id: persona.id, name: next })
    await refetch()
  }

  const handleAnalyze = async () => {
    // Confirm with the user before discarding edits to an existing brief.
    if (persona.analysisBrief && persona.analysisBrief.blocks.length > 0) {
      const ok = confirm(
        'Regenerating will replace your current brief with a fresh AI version. Any edits you made will be lost. Continue?',
      )
      if (!ok) return
    }
    setAnalysisOpen(true)
    setShowStreaming(true)
    try {
      await startAnalyze(true, enabledAnalyzers)
      await refetch()
    } finally {
      setShowStreaming(false)
    }
  }

  const hasBrief = !!persona.analysisBrief && persona.analysisBrief.blocks.length > 0

  // User-edited brief writes through to the same column via the update mutation.
  const handleBriefChange = async (next: MoodboardBrief) => {
    await update({ id: persona.id, analysisBrief: next })
    await refetch()
  }

  // The Analyze / Regenerate CTA now lives at the right end of the canvas toolbar
  // (see AddItemToolbar's `trailing` slot), not the header. Onboarding uses its own
  // "Finalize & continue" button in the header instead.
  const analyzeButton = (
    <Button
      onClick={handleAnalyze}
      disabled={analyzing || enrichingIds.size > 0}
      size="sm"
      className="gap-1.5 rounded-l-full rounded-r-md"
      title={
        enrichingIds.size > 0
          ? `Reading ${enrichingIds.size} item${enrichingIds.size === 1 ? '' : 's'} in the background…`
          : undefined
      }>
      {analyzing || enrichingIds.size > 0 ? (
        <Loader2Icon size={14} className="animate-spin" />
      ) : hasBrief ? (
        <RotateCwIcon size={14} />
      ) : (
        <SparklesIcon size={14} />
      )}
      {analyzing
        ? 'Analyzing…'
        : enrichingIds.size > 0
          ? `Enriching ${enrichingIds.size}…`
          : hasBrief
            ? 'Regenerate'
            : 'Analyze'}
    </Button>
  )

  // Analyze CTA split (Regenerate + analyzer picker). It's the action that
  // produces the brief, so it lives beside the toolbar while the brief is
  // closed and moves into the brief sidebar header once it's open.
  const analyzeSplit = (
    <div className="flex items-center gap-px rounded-full shadow-lg">
      {analyzeButton}
      <AnalyzeConfigPopover
        enabled={enabledAnalyzers}
        onToggle={toggleAnalyzer}
        disabled={analyzing}
      />
    </div>
  )

  // Toolbar trailing: the Regenerate split + the Brief toggle. Both leave the
  // toolbar while the brief is open (Regenerate relocates to the sidebar; the
  // sidebar carries its own close button), so onboarding and the open state
  // both suppress it.
  const toolbarTrailing =
    isOnboarding || analysisOpen ? undefined : (
      <div className="flex items-center gap-2">
        {analyzeSplit}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setAnalysisOpen(true)}
          className="bg-card/95 text-muted-foreground hover:text-foreground gap-1.5 rounded-full shadow-lg backdrop-blur"
          aria-label="Open the AI brief">
          <ScrollTextIcon size={14} />
          Brief
        </Button>
      </div>
    )

  return (
    <section
      // Outside onboarding the layout's phone bar (h-12) sits above the canvas.
      className={`flex w-full flex-col ${isOnboarding ? 'h-[100dvh]' : 'h-[calc(100dvh-3rem)] md:h-[100dvh]'}`}>
      <div className="flex min-h-0 flex-1">
        <div className="relative min-w-0 flex-1">
          <Canvas
            isEmpty={items.length === 0 && pendingItems.length === 0 && draft === null}
            onDrop={handleDrop}
            onMarquee={handleMarquee}
            dragHalos={Object.keys(dragOverrides)
              .map((id) => {
                const item = items.find((i) => i.id === id)
                const override = dragOverrides[id]
                if (!item || !override) return null
                // Pass the item's current bbox so the halo wraps the silhouette.
                return {
                  x: override.position.x,
                  y: override.position.y,
                  w: item.size.w,
                  h: item.size.h,
                }
              })
              .filter((r): r is Rect => r !== null)}>
            {items.map((item) => {
              const onMetaChange = (patch: {
                polarity?: MoodboardItem['polarity']
                annotation?: MoodboardItem['annotation']
                role?: MoodboardItem['role']
              }) => updateItem(item.id, patch)
              if (item.type === 'text') {
                return (
                  <StickyNote
                    key={item.id}
                    item={item}
                    allItems={items}
                    onMove={(position) => commitItemMove(item.id, position)}
                    onResize={(size) => updateItem(item.id, { size })}
                    onTextChange={(text) => {
                      // An edited starter note is the user's now: drop the seed marker so
                      // the onboarding fill and website changes leave it alone.
                      if (isOnboarding) markEditHintSeen()
                      return updateItem(item.id, { text, seed: undefined })
                    }}
                    onMetaChange={onMetaChange}
                    onAppearanceChange={(patch) => updateItem(item.id, patch)}
                    fill={starterNoteFill(item)}
                    showEditHint={isOnboarding && !!item.seed && !editHintSeen}
                  />
                )
              }
              if (item.type === 'url') {
                return (
                  <LinkCard
                    key={item.id}
                    item={item}
                    allItems={items}
                    onMove={(position) => commitItemMove(item.id, position)}
                    onResize={(size) => updateItem(item.id, { size })}
                    onMetaChange={onMetaChange}
                    onAddLogo={async (candidate: MoodboardLogoCandidate) => {
                      // Drop the materialised logo just to the right of the URL card.
                      // Stagger downward when there are existing items overlapping —
                      // simple linear offset, room for smarter placement later.
                      const position = {
                        x: item.position.x + item.size.w + 24,
                        y: item.position.y,
                      }
                      await addLogoFromCandidate({
                        id: persona.id,
                        candidate,
                        position,
                      })
                      emitDotRipple({ x: position.x, y: position.y, w: 220, h: 220 })
                      await refetchAndRecordHistory()
                    }}
                  />
                )
              }
              if (item.type === 'color') {
                return (
                  <ColorCard
                    key={item.id}
                    item={item}
                    allItems={items}
                    onMove={(position) => commitItemMove(item.id, position)}
                    onResize={(size) => updateItem(item.id, { size })}
                    onChange={(patch) => updateItem(item.id, patch)}
                    onMetaChange={onMetaChange}
                  />
                )
              }
              if (item.type === 'connector') {
                if (reselectingConnectorId === item.id) {
                  // The picker needs far more room than the resting card, so it takes over the
                  // item's anchor point at its own size rather than squeezing into 280×110.
                  return (
                    <div
                      key={item.id}
                      className="absolute"
                      style={{
                        left: item.position.x,
                        top: item.position.y,
                        width: DEFAULT_SIZES.connectorDraft.w,
                        height: DEFAULT_SIZES.connectorDraft.h,
                      }}
                      onPointerDown={(e) => e.stopPropagation()}>
                      <ConnectorPicker
                        provider={item.provider}
                        // Its own target isn't "taken" — re-picking it is a valid no-op.
                        existingTargets={items.flatMap((i) =>
                          i.type === 'connector' && i.id !== item.id ? [i.target] : [],
                        )}
                        onCommit={(value) => commitConnectorReselect(item.id, value)}
                        onCancel={() => setReselectingConnectorId(null)}
                      />
                    </div>
                  )
                }
                return (
                  <ConnectorCard
                    key={item.id}
                    item={item}
                    status={connectorStatusFor(item)}
                    onReselect={() => setReselectingConnectorId(item.id)}
                    allItems={items}
                    onMove={(position) => commitItemMove(item.id, position)}
                    onResize={(size) => updateItem(item.id, { size })}
                    onMetaChange={onMetaChange}
                  />
                )
              }
              return (
                <FileCard
                  key={item.id}
                  item={item}
                  allItems={items}
                  onMove={(position) => commitItemMove(item.id, position)}
                  onResize={(size) => updateItem(item.id, { size })}
                  onMetaChange={onMetaChange}
                />
              )
            })}
            {pendingItems.map((p) => (
              <PendingCard key={p.id} item={p} />
            ))}
            {draft && (
              <DraftCard
                key={draft.id}
                draft={draft}
                existingConnectorTargets={items.flatMap((i) =>
                  i.type === 'connector' ? [i.target] : [],
                )}
                onCommit={handleDraftCommit}
                onCancel={() => setDraft(null)}
              />
            )}
          </Canvas>
          {/* The header bar is gone — its controls float over the fullscreen canvas.
              Top-left offset clears the app's fixed sidebar toggle when the sidebar is
              closed (matches the old header's pl-14). */}
          <div
            title={analysisOpen ? persona.name || 'Untitled' : undefined}
            className={`bg-card/95 border-border/60 absolute top-6 z-10 flex items-center gap-1 rounded-full border py-1 pl-1 shadow-lg backdrop-blur transition-[left] duration-300 ease-in-out ${
              analysisOpen ? 'pr-1' : 'pr-2.5'
            } ${sidebarOpen || isOnboarding ? 'left-4' : 'left-4 md:left-14'}`}>
            {isOnboarding ? (
              // The way back to the language and website steps. /company-setup
              // otherwise resumes straight into this board, which trapped users who
              // pressed browser Back to change their website.
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  canvasStep.track('onboarding_canvas_back_clicked')
                  navigate('/company-setup?edit=1')
                }}
                aria-label="Back to setup"
                className="shrink-0 gap-1 rounded-full px-2 sm:pr-3">
                <ArrowLeftIcon size={16} />
                {/* Icon-only on phones, where the Finalize pill already crowds this one. */}
                <span className="hidden sm:inline">Back</span>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => navigate('/moodboards')}
                className="shrink-0 rounded-full"
                aria-label="Back to moodboards">
                <ArrowLeftIcon size={18} />
              </Button>
            )}
            {/* Renders as plain text (hugs its content, truncates with an ellipsis) until
                clicked, then swaps to an input — a text node has no browser-enforced min
                width, so it can shrink to short names. The invisible sizer span drives the
                width in both modes; edit adds a min-width for comfortable typing and the
                min-width transition animates the grow/shrink on enter/leave. */}
            {/* The name collapses away while the brief sidebar is open — the narrower
                canvas would otherwise shove the centered toolbar into the title. The
                pill's title attr keeps the name reachable on hover. */}
            <div
              className={`items-center transition-[min-width] duration-200 ease-out ${
                analysisOpen ? 'hidden' : 'inline-grid max-w-[min(20rem,40vw)]'
              } ${editingName ? 'min-w-[9rem]' : 'min-w-0'}`}>
              <span
                aria-hidden
                className="pointer-events-none invisible col-start-1 row-start-1 h-8 min-w-[2ch] select-none overflow-hidden whitespace-pre px-3 text-sm font-semibold leading-8">
                {(nameDraft ?? persona.name) || 'Untitled'}
              </span>
              {editingName ? (
                <input
                  autoFocus
                  size={1}
                  value={nameDraft ?? persona.name}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onBlur={() => {
                    void saveName()
                    setEditingName(false)
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
                  }}
                  className="text-foreground focus-visible:border-input focus-visible:bg-background focus-visible:ring-ring col-start-1 row-start-1 h-8 w-full min-w-0 rounded-md border border-transparent bg-transparent px-3 text-sm font-semibold shadow-none outline-none focus-visible:ring-1"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setEditingName(true)}
                  aria-label="Rename moodboard"
                  className="text-foreground hover:bg-hover col-start-1 row-start-1 h-8 w-full min-w-0 truncate rounded-md px-3 text-left text-sm font-semibold">
                  {(nameDraft ?? persona.name) || 'Untitled'}
                </button>
              )}
            </div>
          </div>

          {isOnboarding && (
            <div className="bg-card/95 absolute right-6 top-6 z-10 flex items-center gap-1 rounded-full p-1 shadow-lg backdrop-blur">
              <Button
                onClick={() => {
                  setFinalizeClicked(true)
                  canvasStep.complete({
                    item_count: persona.items.length,
                    still_enriching: enrichingIds.size,
                    still_filling: fillPending,
                  })
                  navigate(`/company-setup/finish?moodboard=${persona.id}`)
                }}
                disabled={finalizeBlocked}
                size="sm"
                className={cn('gap-1.5 rounded-full', nudgeFinalize && 'animate-cta-glow')}
                title={
                  isEnriching
                    ? `Reading ${enrichingIds.size} item${enrichingIds.size === 1 ? '' : 's'} in the background…`
                    : undefined
                }>
                {finalizeBlocked ? (
                  <Loader2Icon size={14} className="animate-spin" />
                ) : (
                  <SparklesIcon size={14} />
                )}
                {finalizeBlocked ? finalizeBusyLabel : 'Finalize & continue'}
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => setAnalysisOpen((o) => !o)}
                className="text-muted-foreground hover:text-foreground rounded-full"
                aria-label={analysisOpen ? 'Close the AI brief' : 'Open the AI brief'}>
                <ScrollTextIcon size={16} />
              </Button>
            </div>
          )}

          <AddItemToolbar
            onAddText={onAddText}
            onAddUrl={onAddUrl}
            onAddColor={onAddColor}
            onUploadFiles={onUploadFiles}
            onAddConnector={onAddConnector}
            trailing={toolbarTrailing}
          />
          <CanvasControls
            contentRects={items.map((i) => ({
              x: i.position.x,
              y: i.position.y,
              w: i.size.w,
              h: i.size.h,
            }))}
            canUndo={history.canUndo()}
            canRedo={history.canRedo()}
            onUndo={handleUndo}
            onRedo={handleRedo}
          />
          {isOnboarding && tutorial.open && (
            <OnboardingCanvasTutorial onDismiss={tutorial.dismiss} />
          )}
        </div>

        <aside
          aria-hidden={!analysisOpen}
          style={{ width: analysisOpen ? `${analysisWidth}px` : '0px' }}
          className={`border-border/60 bg-background/60 relative shrink-0 overflow-hidden border-l ${
            isResizing ? '' : 'transition-[width] duration-300 ease-in-out'
          }`}>
          {/* Fixed-width flex column so the header + content don't reflow while the
              sidebar animates open/closed. Header carries the relocated Regenerate
              split (left) and the close button (right). */}
          <div className="flex h-full flex-col" style={{ width: `${analysisWidth}px` }}>
            {analysisOpen && (
              <div className="flex shrink-0 items-center justify-between gap-2 px-4 pb-2 pt-4">
                {analyzeSplit}
                <Button
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => setAnalysisOpen(false)}
                  className="text-muted-foreground hover:text-foreground shrink-0 rounded-full"
                  aria-label="Close the AI brief">
                  <XIcon size={16} />
                </Button>
              </div>
            )}
            <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-1">
              <AnimatePresence mode="wait" initial={false}>
                {showStreaming ? (
                  <motion.div
                    key="streaming"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.22, ease: 'easeOut' }}>
                    <StreamingAnalysisPanel
                      state={analyzeState}
                      enabledAnalyzers={enabledAnalyzers}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="brief"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.28, ease: 'easeOut' }}>
                    <AnalysisPanel
                      brief={persona.analysisBrief}
                      generatedAt={persona.analysisGeneratedAt}
                      stale={stale}
                      onBriefChange={handleBriefChange}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          {/* Drag the left edge to resize. Hover surface is 4px wide; the visual hairline
              only colours up on hover / during drag so it doesn't compete with content. */}
          {analysisOpen && (
            <div
              onPointerDown={onResizeStart}
              onPointerMove={onResizeMove}
              onPointerUp={onResizeEnd}
              onPointerCancel={onResizeEnd}
              className="hover:bg-primary/40 group absolute left-0 top-0 z-10 h-full w-1 cursor-ew-resize touch-none transition-colors"
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize analysis sidebar"
            />
          )}
        </aside>
      </div>
    </section>
  )
}
